"use client";

import { t } from "@/lib/localization";
import { FlipCard, FlipCardsAppearance } from "@/lib/utils/lomdaV3/types";
import { useResolvedInnerGap } from "../../../../hooks/useGridGap";
import { GrowingTextarea } from "../../shared/growingTextarea";
import { CardFrame, CardIcon, CardIconPicker } from "./cardFace";

const titleClass = "lomda-v3-text-h5 whitespace-pre-wrap break-words";
const textClass =
    "lomda-v3-text-p whitespace-pre-wrap break-words text-[hsl(var(--lomda-muted-text))]";

export function StaticPreviewCard({
    card,
    appearance,
}: {
    card: FlipCard;
    appearance: FlipCardsAppearance;
}) {
    const innerGap = useResolvedInnerGap();
    const title = card.title.trim();
    const text = card.subtitle.trim();
    return (
        <CardFrame
            card={card}
            appearance={appearance}
            className="flex flex-col"
            style={{ gap: innerGap }}
        >
            {card.icon && <CardIcon icon={card.icon} appearance={appearance} />}
            {(title || text) && (
                <div className="flex flex-col" style={{ gap: innerGap / 2 }}>
                    {title && <h3 className={titleClass}>{card.title}</h3>}
                    {text && <p className={textClass}>{card.subtitle}</p>}
                </div>
            )}
        </CardFrame>
    );
}

export function StaticEditCard({
    card,
    appearance,
    focused,
    onFocus,
    onPatch,
}: {
    card: FlipCard;
    appearance: FlipCardsAppearance;
    focused: boolean;
    onFocus: () => void;
    onPatch: (patch: Partial<FlipCard>) => void;
}) {
    const innerGap = useResolvedInnerGap();
    return (
        <CardFrame
            card={card}
            appearance={appearance}
            focused={focused}
            onFocus={onFocus}
            className="flex flex-col"
            style={{ gap: innerGap }}
        >
            <CardIconPicker
                icon={card.icon}
                appearance={appearance}
                onPick={(icon) => onPatch({ icon })}
            />
            <div className="flex flex-col" style={{ gap: innerGap / 2 }}>
                <GrowingTextarea
                    value={card.title}
                    onValueChange={(title) => onPatch({ title })}
                    placeholder={t("lomda-v3-editor.flip.card-title-ph")}
                    className={titleClass}
                />
                <GrowingTextarea
                    value={card.subtitle}
                    onValueChange={(subtitle) => onPatch({ subtitle })}
                    placeholder={t("lomda-v3-editor.flip.card-description-ph")}
                    className={textClass}
                />
            </div>
        </CardFrame>
    );
}
